// ============================================
// Auth Store (Zustand)
// ============================================

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'manager' | 'user';
  tenantMemberships?: Array<{
    tenantId: string;
    role: 'manager' | 'user';
    isPrimary: boolean;
  }>;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  expiresAt: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setAuth: (token: string, user: AuthUser, expiresAt: string) => void;
  updateUser: (user: Partial<AuthUser>) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  checkTokenExpiry: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      expiresAt: null,
      isAuthenticated: false,
      isLoading: true, // Start loading until we check persisted state

      setAuth: (token, user, expiresAt) => {
        set({
          token,
          user,
          expiresAt,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      updateUser: userData => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          });
        }
      },

      clearAuth: () => {
        set({
          token: null,
          user: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: loading => {
        set({ isLoading: loading });
      },

      checkTokenExpiry: () => {
        const { expiresAt, clearAuth } = get();
        if (!expiresAt) {
          return false;
        }

        const expiryDate = new Date(expiresAt);
        const now = new Date();

        // Check if token is expired or will expire in the next minute
        if (expiryDate.getTime() - now.getTime() < 60 * 1000) {
          clearAuth();
          return false;
        }

        return true;
      },
    }),
    {
      name: 'muraka-auth',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => state => {
        // After rehydrating, check if the token is still valid
        if (state) {
          state.setLoading(false);
          if (state.token) {
            state.checkTokenExpiry();
          }
        }
      },
    }
  )
);
